import { useDrop } from 'react-dnd'
import { useState } from 'react'
import { VendorCard, Vendor } from './VendorCard'

interface KanbanColumnProps {
  id: string
  title: string
  vendors: Vendor[]
  onDrop: (vendor: Vendor, sourceColumn: string, targetColumn: string) => void
}

export function KanbanColumn({ id, title, vendors, onDrop }: KanbanColumnProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'VENDOR',
    drop: (item: { vendor: Vendor; sourceColumn: string }) => {
      onDrop(item.vendor, item.sourceColumn, id)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }))

  if (isCollapsed) {
    return (
      <div className="flex flex-col shrink-0 w-12">
        <div
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg h-[400px] flex flex-col items-center justify-start cursor-pointer py-4 px-3 relative hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          onClick={() => setIsCollapsed(false)}
        >
          <svg className="w-4 h-4 text-slate-600 dark:text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="flex-1 flex items-center justify-center">
            <h3 
              className="font-medium text-sm text-slate-900 dark:text-slate-50 whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {title}
            </h3>
          </div>
          <span className="bg-slate-200 dark:bg-slate-700 rounded-full px-1.5 py-0.5 font-medium text-xs text-slate-700 dark:text-slate-300 mt-3">
            {vendors.length}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col shrink-0 w-[280px]">
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-t-lg px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        onClick={() => setIsCollapsed(true)}
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-50">
            {title}
          </h3>
        </div>
        <span className="bg-slate-200 dark:bg-slate-700 rounded-full px-2 py-0.5 font-medium text-xs text-slate-700 dark:text-slate-300">
          {vendors.length}
        </span>
      </div>
      <div
        ref={drop}
        className={`bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 border-t-0 rounded-b-lg p-3 flex flex-col gap-2 min-h-[200px] transition-colors ${
          isOver ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700' : ''
        }`}
      >
        {vendors.map((vendor) => (
          <VendorCard key={vendor.id} vendor={vendor} columnId={id} />
        ))}
        {vendors.length === 0 && !isOver && (
          <div className="flex items-center justify-center h-32 text-sm text-slate-400 dark:text-slate-600">
            Drag vendors here
          </div>
        )}
      </div>
    </div>
  )
}

